// src/components/operationsAssociate/ViewItemHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = "https://ims.holdthechild.net";

// The prop is now `item` instead of `itemId`
export default function ViewItemHistoryModal({ item, closeModal }) {
    const [historyData, setHistoryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch the item details with populated history when the modal opens
    useEffect(() => {
        const fetchItemDetails = async () => {
            setLoading(true);
            try {
                // We now check if `item` exists before making the API call
                if (!item || !item._id) {
                    setError("Item data is missing. Cannot fetch history.");
                    setLoading(false);
                    return;
                }
                const token = localStorage.getItem("token");
                // Use `item._id` from the prop to make the API call
                const response = await axios.get(`${API_URL}/api/inventory/${item._id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setHistoryData(response.data.data);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch item history.");
                toast.error("Failed to load history.");
                setLoading(false);
            }
        };
        fetchItemDetails();
    }, [item]); // Dependency array should be `item`

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <p className="text-center text-gray-500">Loading history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <p className="text-center text-red-500">{error}</p>
                </div>
            </div>
        );
    }

    // Sort history to show most recent first
    const sortedHistory = [...historyData.history].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-2xl font-bold text-orange-600">History for "{historyData.name}"</h2>
                    <button type="button" onClick={closeModal} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                </div>

                {sortedHistory.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                        {sortedHistory.map((log, index) => (
                            <li key={index} className="py-4">
                                <div className="flex items-center space-x-4">
                                    <div className={`p-2 rounded-full ${log.type === 'Received' ? 'bg-green-100 text-green-600' :
                                        log.type === 'Issued' ? 'bg-red-100 text-red-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                        {log.type === 'Received' ? '+' : '-'} {log.quantity}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-gray-800">{log.type} {log.quantity} {historyData.unitOfMeasure}</p>
                                        <p className="text-sm text-gray-500">
                                            By <span className="font-semibold text-gray-700">{log.actor?.firstName} {log.actor?.lastName}</span> on {moment(log.timestamp).format('MMMM Do YYYY, h:mm a')}
                                            {log.type === 'Received' && log.vendor && ( // ADDED: Display vendor for received items
                                                <span className="block mt-1">
                                                    From Vendor: <span className="font-semibold text-gray-700">{log.vendor.name}</span>
                                                </span>
                                            )}
                                            {log.projectId ? (
                                                <span className="block mt-1">
                                                    Assigned to project: <span className="font-semibold text-indigo-600">{log.projectId.projectTitle}</span> (Program Manager: <span className="font-semibold text-gray-700">{log.assignedTo?.firstName} {log.assignedTo?.lastName}</span>)
                                                </span>
                                            ) : log.assignedTo ? (
                                                <span className="block mt-1">
                                                    Assigned to staff: <span className="font-semibold text-gray-700">{log.assignedTo?.firstName} {log.assignedTo?.lastName}</span>
                                                </span>
                                            ) : null}
                                        </p>
                                        {log.details && <p className="mt-1 text-sm text-gray-600 italic">"{log.details}"</p>}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-center text-gray-500 italic">No history available for this item.</div>
                )}

                <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200">
                    <button type="button" onClick={closeModal} className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Close</button>
                </div>
            </div>
        </div>
    );
}